import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Legend, Line, LabelList, Tooltip } from 'recharts';
import { CurveType } from "recharts/types/shape/Curve";
import { HorizontalAlignmentType, VerticalAlignmentType } from "recharts/types/component/DefaultLegendContent";
import { CategoricalChartState } from 'recharts/types/chart/types';
import { useTranslation } from 'react-i18next';
import dayjs from "dayjs";

import { Query } from 'system/query/Query';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { defaultSettings } from './TrendChartSettings';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import { MakeDrilldownRangeQuery } from 'components/widgets/utility/DrilldownQuery';
import { FlattenedResponseAgg } from 'endpoints/Agg';
import TrendChartTooltip from './TrendChartTooltip';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getActiveWidgetRequestsByName, getSingleWidgetResponseByName, getWidgetResponsesByName, widgetQueryTitleByResponses, WidgetResponseItem } from 'system/widget/Widget';

export function getAxisTitleFromResponse(response: FlattenedResponseAgg): { xLabel: string, yLabel: string } {
  if (!response || !Object.keys(response).length) {
    return { xLabel: "", yLabel: "" };
  }
  const firstAggKey = Object.keys(response)[0];
  if (!firstAggKey) {
    return { xLabel: "", yLabel: "" };
  }
  const firstAgg = response[firstAggKey];
  const lastSeriesIndex = (firstAgg.series?.length ?? 1) - 1;
  const xLabel = firstAgg.series?.[0].field_label ?? "";
  let yLabel = firstAgg.series?.[lastSeriesIndex].field_label ?? "";
  if (firstAgg.series?.length === 1) {
    yLabel = firstAgg.series[0].label;
  }
  return { xLabel, yLabel };
}

export interface TrendEvent {
  fillColor: string;
  movingAverage: number;
  showEvent: boolean;
  standardDeviation: number;
  textColor: string;
}

export interface LineChartData {
  data: Array<any>;
  dataKeys: Array<string>;
  xIsNumeric: boolean;
}

function buildLineChartData(apiResponses: WidgetResponseItem[] | undefined): LineChartData {
  const uniqueQueryNames = apiResponses?.map(reponse => reponse.queryDisplayTitle || "Query") || [];
  const responses = apiResponses?.map(response => response.responseBody as FlattenedResponseAgg) || [];

  const lineChartData: LineChartData = {
    data: [],
    dataKeys: uniqueQueryNames,
    xIsNumeric: false
  }

  for (let responseIndex = 0; responseIndex < responses.length; responseIndex++) {
    const aggResponse = responses[responseIndex];

    const firstAggKey = Object.keys(aggResponse)[0];
    if (!firstAggKey) {
      // It is possible to get bad data from the ResponseAgg.  If this is the case escape early.
      break;
    }
    const aggResults = aggResponse[firstAggKey];
    const dataLength = aggResults.labels?.length ?? 0;

    for (let dataIndex = 0; dataIndex < dataLength; dataIndex++) {
      let key = aggResults.keys[dataIndex];
      const seriesIndex = aggResults.series.length > 1 ? 1 : 0
      const label = aggResults.labels[dataIndex];
      const value = aggResults.series[seriesIndex].values[dataIndex] || 0;

      if (typeof (key) === "string" && dayjs(key).isValid()) {
        key = dayjs(key).valueOf();
      }
      // if numeric, mark the data as such
      if (typeof (key) === "number") {
        lineChartData.xIsNumeric = true;
      }
      // Find a bucket that matches the key, if not insert one at the proper place in the array.
      // This is necessary because the data may not be in the same order for each query.
      const currentRow = lineChartData.data.find((bucket) => {
        return bucket["name"] === key
      });
      if (currentRow) {
        currentRow[uniqueQueryNames[responseIndex]] = value;
      }
      if (!currentRow) {
        const newRow = {
          label: label,
          name: key,
          originalKey: aggResults.keys[dataIndex]
        };
        newRow[uniqueQueryNames[responseIndex]] = value;

        // Find the proper place to insert the new row.
        if (lineChartData.data.length === 0) {
          lineChartData.data.push(newRow);
        } else {
          let insertIndex = 0;
          for (let i = 0; i < lineChartData.data.length; i++) {
            if (lineChartData.data[i].key > key) {
              insertIndex = i;
              break;
            }
          }
          lineChartData.data.splice(insertIndex, 0, newRow);
        }
      }
    }
  }
  // sort the data by name if it is numeric
  if (lineChartData.xIsNumeric) {
    lineChartData.data.sort((a, b) => a.name - b.name);
  }
  return lineChartData;
}

export function renderTrendEvent(props: any): React.JSX.Element | null {
  const radius = 10;
  const trendEvent: TrendEvent = props.value;
  if (!trendEvent || !trendEvent.showEvent) {
    return null;
  }
  return (
    <g>
      <circle cx={props.x} cy={props.y} r={radius} fill={trendEvent.fillColor} />
      <text x={props.x} y={props.y + 1} fontWeight="bold" fontSize="1.2em" fill={trendEvent.textColor} textAnchor="middle" dominantBaseline="middle">
        𝐢
      </text>
    </g>
  );
}

function CustomizedXAxisTick(props: any): React.JSX.Element {
  const { x, y, lineChartData } = props;
  const row = lineChartData.data[props.payload.index];

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#666">
        {row.label}
      </text>
    </g>
  );
}

function TrendChartContent(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const { i18n, t } = useTranslation();

  const firstApiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'trend-chart');
  const firstResponseBody = firstApiResponseInfo?.responseBody || {};

  const apiResponses = getWidgetResponsesByName(props.apiResponses, "trend-chart");
  const apiWidgetRequests = getActiveWidgetRequestsByName(props.activeWidgetRequests, "trend-chart");

  const chartColors = theme.colors.namedColors.chartColorArray;
  const numChartColors = chartColors.length;

  const [hoveredLineIndex, setHoveredLineIndex] = React.useState<number | null>(null);
  const [drilldownTitle, setDrilldownTitle] = React.useState<string | null>(null);
  const [drilldownQuery, setDrilldownQuery] = React.useState<Query | null>(null);
  const [drilldownQueryIds, setDrilldownQueryIds] = React.useState<string[] | null>(firstApiResponseInfo?.originatingRequest?.activeQueryIds || []);
  
  const [hasTooManyRowsError, setHasTooManyRowsError] = useState<boolean>(false);

  const lineChartRef = useRef(null);

  const [tooltipState, setTooltipState] = useState<CategoricalChartState>();
  const isInLayoutMode = props.isInLayoutMode ?? false;
  const currentState = { ...defaultSettings, ...props.widget?.data?.settings };
  const legendWrapperStyle = currentState.verticalAlign === "Top" ? { top: 10 } : {};

  const lineChartData = useMemo(() => {
    // Check if any of the responses are too long to process
    const maxDataPoints = (props.contentRef.current.clientWidth * 2) || 5000;
    const hasTooManyDataPoints = apiResponses?.some(response => {
      return maxDataPoints < (response?.responseBody?.histogram?.keys?.length ?? 0);
    });
    if (hasTooManyRowsError !== hasTooManyDataPoints) {
      setHasTooManyRowsError(!!hasTooManyDataPoints);
      return { data: [], dataKeys: [], xIsNumeric: false };
    } else {
      return buildLineChartData(apiResponses);
    }
  }, [apiResponses, props.contentRef.current.clientWidth]);

  useEffect(() => {
    const movingAvgPeriod = 3;
    const lineLabels = lineChartData.dataKeys ?? [];
    const lineCount = lineChartData.dataKeys.length;
    const lineData = lineChartData.data ?? [];
    const dataCount = lineChartData.data.length ?? 0;

    if (!lineChartData || !lineCount || !dataCount) {
      return;
    }

    // Individual trend lines may have a different start or end date.
    const lineStartIndices = new Array(lineCount).fill(-1);
    const lineEndIndices = new Array(lineCount).fill(dataCount - 1);

    // Calculate the start and end ranges for each line.
    for (let lineIndex = 0; lineIndex < lineCount; lineIndex++) {
      const valueKey = lineLabels[lineIndex];
      // Since a single point holds the data for every line, we must use a unique key to identify
      // the trend data for the respective line.
      const trendEventKey = `${valueKey}_trendEvent`;
      for (let pointIndex = 0; pointIndex < dataCount; pointIndex++) {
        const point = lineData[pointIndex];
        const value = point[valueKey] || 0;
        if (Number.isFinite(value)) {
          // We are either starting the range or continuing the range.
          if (lineStartIndices[lineIndex] === -1) {
            // The start hasn't been set, so this is the start of the range.
            lineStartIndices[lineIndex] = pointIndex;
          }
        } else {
          // We are either ending the range or haven't began the range.
          if (lineStartIndices[lineIndex] >= 0 && lineEndIndices[lineIndex] === (dataCount - 1)) {
            // The start has been set, so this is the end of the range.
            lineEndIndices[lineIndex] = pointIndex - 1;
          }
        }
        // Every point needs trend event information, even if a point may not have data for a
        // particular line.
        // Create an initial TrendEvent for this point.  Its values will be updated in subsequent
        // steps of the standard deviation calculation.
        const trendEvent: TrendEvent = {
          fillColor: chartColors[lineIndex % numChartColors],
          movingAverage: 0,
          showEvent: false,
          standardDeviation: 0,
          textColor: theme.palette.common.white
        };
        point[trendEventKey] = trendEvent;
      }

      // Trend events are points on the trend line with a moving average with at least one positive
      // standard deviation.
      const startIndex = lineStartIndices[lineIndex];
      const endIndex = lineEndIndices[lineIndex]
      const pointCount = endIndex - startIndex + 1;

      // Skip invalid ranges so downstream math doesn't produce NaN.
      if (startIndex < 0 || endIndex < startIndex || pointCount <= 0) {
        continue;
      }

      // 1. Calculate the moving average for each point.  This is the metric the standard deviation
      // is based on.
      for (let pointIndex = startIndex; pointIndex <= endIndex; pointIndex++) {
        if (pointIndex < startIndex + movingAvgPeriod) {
          // We don't have enough data points to determine a moving average.
          continue;
        }
        const point = lineData[pointIndex];
        const trendEvent = point?.[trendEventKey];
        let movingAverage = 0;
        for (let movingAvgIndex = pointIndex - movingAvgPeriod; movingAvgIndex < pointIndex; movingAvgIndex++) {
          const movingAveragePoint = lineData[movingAvgIndex];
          const value = movingAveragePoint?.[valueKey] || 0;
          movingAverage += value;
        }
        movingAverage /= movingAvgPeriod;
        trendEvent.movingAverage = movingAverage;
      }

      // 2. Find the mean given the data range for the line.
      let mean = 0;
      for (let pointIndex = startIndex; pointIndex <= endIndex; pointIndex++) {
        const point = lineData[pointIndex];
        const trendEvent = point?.[trendEventKey];
        mean += trendEvent?.movingAverage || 0;
      }
      mean /= pointCount;

      // 3. Calculate the total deviation of each data point from the mean.
      let totalDeviation = 0;
      for (let pointIndex = startIndex; pointIndex <= endIndex; pointIndex++) {
        const point = lineData[pointIndex];
        const trendEvent = point?.[trendEventKey];
        totalDeviation += Math.pow((trendEvent?.movingAverage || 0) - mean, 2);
      }

      // 4. The variance is the mean of the deviations.
      const variance = totalDeviation / pointCount;

      // 5. The standard deviation is equal to the square root of the variance. 
      const standardDeviation = Math.sqrt(variance);
      const safeStandardDeviation = Number.isFinite(standardDeviation) && standardDeviation > 0
        ? standardDeviation : 0;

      // 6. Calculate how many standard deviations each point is from the mean.
      for (let pointIndex = startIndex + movingAvgPeriod; pointIndex <= endIndex; pointIndex++) {
        const point = lineData[pointIndex];
        const value = point[valueKey];
        const trendEvent = point?.[trendEventKey];
        const calculatedStandardDeviation = safeStandardDeviation > 0
          ? (value - mean) / safeStandardDeviation : 0;

        // If the standard deviation is not a finite number, set it to zero to prevent issues with rendering 
        trendEvent.standardDeviation = Number.isFinite(calculatedStandardDeviation)
          ? calculatedStandardDeviation : 0;

        // Validate the trend event for display.
        if (trendEvent.standardDeviation < 1) {
          // Only show trend events with a positive standard deviations of at least one.
          continue;
        }
        const previousPoint = lineData[pointIndex - 1];
        const previousValue = previousPoint[valueKey];
        if (value <= previousValue || value <= trendEvent.movingAverage) {
          // Only show trend events with an increasing value, above the moving average.
          continue;
        }
        trendEvent.showEvent = true;
      }
    }
  }, [lineChartData]);


  const { xLabel, yLabel } = getAxisTitleFromResponse(firstResponseBody);
  const chartTitle = t("multi-axis-title", { xLabel, yLabel });

  const trendChartTitleOptions = [
    { "x-axis": xLabel ?? "" },
    { "y-axis": yLabel ?? "" },
  ];

  const widgetQueryTitle = widgetQueryTitleByResponses(props.apiResponses, "trend-chart", i18n);

  return (
    <>
      {hasTooManyRowsError &&
        <WidgetAlert alertTitleText={i18n.t("widget-types.trend-chart.too-many-points")} severity="warning" />
      }
      {!hasTooManyRowsError && lineChartData.data.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              settingsTitle={currentState.widgetTitle}
              defaultTitle={chartTitle}
              customTitle={currentState.customWidgetTitle}
              widgetQueryTitle={widgetQueryTitle}
              widgetTitleOptions={trendChartTitleOptions}
            />
          }
        >
          <TrendChartTooltip state={tooltipState} xAxisLabelField="label">
            <Box
              sx={{
                height: "100%",
                overflow: "hidden"
              }}>
              <ResponsiveContainer>
                <LineChart data={lineChartData.data}
                  ref={lineChartRef}
                  points={lineChartData.points}
                  onClick={(nextState: CategoricalChartState) => {
                    const lineIndex = hoveredLineIndex || 0;
                    const selectedResponse = apiResponses?.[lineIndex];
                    const field = selectedResponse?.originatingRequest?.requestInit?.body?.aggs?.histogram?.field;
                    const startValue = nextState?.activePayload?.[0]?.payload?.["originalKey"];
                    const startLabel = nextState?.activePayload?.[0]?.payload?.["label"];
                    const histogram = selectedResponse?.responseBody?.histogram || [];
                    const rowIndex = histogram?.keys?.findIndex((key: any) => key === startValue);
                    const endValue = histogram?.keys?.[rowIndex + 1];
                    const endLabel = histogram?.labels?.[rowIndex + 1];

                    const drilldownInfo = MakeDrilldownRangeQuery(field, startValue, startLabel, endValue, endLabel);
                    if (drilldownInfo) {
                      setDrilldownTitle(drilldownInfo?.title);
                      setDrilldownQuery(drilldownInfo?.query);
                    }
                    
                    const widgetRequests = Array.isArray(apiWidgetRequests) ? apiWidgetRequests[lineIndex] : apiWidgetRequests;
                    setDrilldownQueryIds(widgetRequests?.activeQueryIds || null);
                  }}
                  onMouseMove={(nextState: CategoricalChartState, ...args) => {
                    if (lineChartData.dataKeys.length > 1) {
                      const hoveredValues = nextState?.activePayload?.map((payload: any, index: number) => {
                        return {
                          value: payload?.value,
                          index: index
                        };
                      }).filter((payload: any) => payload.value !== undefined);

                      if (!hoveredValues?.length || !lineChartRef.current) {
                        return;
                      } else if (hoveredValues?.length === 1) {
                        setHoveredLineIndex(hoveredValues[0].index);
                      }

                      const maxYValue = lineChartRef.current?.state.yAxisMap[0].domain[1];
                      const height = lineChartRef.current?.state.yAxisMap[0].height;
                      const chartY = height - nextState?.chartY;
                      // convert chartY to a value using the maxYValue
                      const mouseValue = (chartY / height) * maxYValue;
                      // find the nearest point to the value
                      const nearestPoint = hoveredValues.reduce((memo, point) => {
                        if (point.value === undefined) {
                          return memo;
                        }
                        const diff = Math.abs(point.value - mouseValue);
                        if (memo.index === undefined || diff < memo.diff) {
                          return { index: point.index, diff: diff };
                        }
                        return memo;
                      }, { index: undefined, diff: undefined });

                      setHoveredLineIndex(nearestPoint.index);
                    } else {
                      setHoveredLineIndex(0);
                    }
                    setTooltipState(nextState)
                  }}
                  onMouseLeave={(nextState: CategoricalChartState) => { setTooltipState(nextState); setHoveredLineIndex(null); }}
                  margin={{ top: 10, right: 30, left: 30, bottom: 10 }}
                  style={{ cursor: "pointer" }}
                >
                  {/*
                This component still needs a Recharts Tooltip because the Recharts Tooltip handles
                drawing the vertical line and highlights the dot where the user's cursor is.
                */}
                  <Tooltip content={() => null} />
                  {currentState.showGrid && <CartesianGrid strokeDasharray="5 5" strokeOpacity={0.65} />}
                  <XAxis
                    dataKey="name"
                    includeHidden
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={true}
                    axisLine={!!currentState.xAxis}
                    tickLine={!!currentState.xAxis}
                    tick={currentState.xAxis ? <CustomizedXAxisTick lineChartData={lineChartData} /> : false}
                    scale="linear"
                    interval="preserveStartEnd"
                  />
                  {currentState.yAxis && <YAxis />}
                  {currentState.showLegend &&
                    <Legend
                      wrapperStyle={legendWrapperStyle}
                      align={currentState.horizontalAlign as HorizontalAlignmentType}
                      verticalAlign={currentState.verticalAlign.toLowerCase() as VerticalAlignmentType} />
                  }
                  {lineChartData.dataKeys.map((segmentLabel: string, index: number) => {
                    return (
                      <Line
                        activeDot={{ r: 7, stroke: chartColors[index] }}
                        type={currentState.lineType as CurveType}
                        key={index}
                        dataKey={segmentLabel}
                        dot={currentState.showDots && { r: 5 }}
                        stroke={chartColors[index] as string}
                        opacity={(hoveredLineIndex === index) ? 1 : currentState.lineOpacity}
                        strokeWidth={(hoveredLineIndex === index) ? currentState.lineWidth * 2 : currentState.lineWidth}
                        onMouseOver={() => setHoveredLineIndex(index)}
                        isAnimationActive={!isInLayoutMode}>
                        {currentState.showEvents &&
                          <LabelList
                            dataKey={`${segmentLabel + "_trendEvent"}`}
                            content={renderTrendEvent} />
                        }
                      </Line>
                    );
                  })
                  }
                </LineChart>
              </ResponsiveContainer>
              {drilldownQuery && drilldownTitle &&
                <DashboardQueryDrilldownDialog
                  title={drilldownTitle}
                  queryInfo={props.queryInfo}
                  onClose={() => setDrilldownQuery(null)}
                  activeQueryIds={drilldownQueryIds || []}
                  drilldownQuery={drilldownQuery}
                  widgetRequestInfo={props.widget?.request_info}
                />
              }
            </Box>
          </TrendChartTooltip>
        </WidgetLayout>
      }
      {lineChartData.data.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(TrendChartContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});